import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import StaticPageHeader from '../components/common/StaticPageHeader';
import { callGetWebservice, callPostWebservice } from '../hooks/api';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatTime, validatePhone, getParsedPhone } from '../utils/Utilities';
import CustomInput from '../components/common/CustomInput';
import PhoneInputGroup from '../components/common/PhoneInputGroup';
import { Helmet } from 'react-helmet-async';

const BookEvent = () => {
    const [outlets, setOutlets] = useState([]);
    const [countries, setCountries] = useState([]);
    const merchantSettings = useSelector((state) => state.general.merchantSettings);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        let requestPacket = { 'event': true };
        callGetWebservice('outlets', requestPacket, (response) => {
            setOutlets(response.data.outlets);
        }, null, dispatch, navigate);

        callGetWebservice('countries', {}, (response) => {
            setCountries(response.data);
        });
    }, [dispatch, navigate]);

    const validationSchema = React.useMemo(() => Yup.object({
        contact_name: Yup.string().required("Customer Name is required"),
        contact_email: Yup.string()
            .email("Invalid email address")
            .required("Customer Email is required"),
        contact_mobile: Yup.string()
            .required('Customer Mobile is required')
            .test('is-valid-phone', 'Use a national or international number valid for the selected country.', function (value) {
                const { country_id } = this.parent;
                if (!country_id || !value) return true;
                const country = countries.find(c => c.id === country_id);
                if (country) {
                    return validatePhone(value, country.iso2);
                }
                return true;
            }),
        country_id: Yup.string().required("Country is required"),
        outlet_id: Yup.string().required("Please select an outlet"),
        person_count: Yup.number()
            .min(1, 'Minimum 1 guest required')
            .required("Number of Guests is required"),
        reservation_date: Yup.date().required("Reservation Date is required"),
        start_time: Yup.string().required("Arrival Time is required"),
    }), [countries]);

    const formik = useFormik({
        initialValues: {
            contact_name: "",
            contact_email: "",
            contact_mobile: "",
            country_id: "",
            outlet_id: "",
            person_count: "",
            reservation_date: "",
            start_time: "",
        },
        validationSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            const country = countries.find(c => c.id === values.country_id);
            const parsedPhone = country ? getParsedPhone(values.contact_mobile, country.iso2) : null;
            const formattedPhone = parsedPhone ? parsedPhone.number : values.contact_mobile;
            const contactCountry = values.country_id;

            const requestData = {
                ...values,
                contact_mobile: formattedPhone,
                contact_country : contactCountry,
                reservation_date: formatDate(values.reservation_date),
                start_time: formatTime(values.start_time),
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                merchantId: merchantSettings?.id
            };

            callPostWebservice('bookings', requestData, (response) => {
                setSubmitting(false);
                resetForm();
                alert('Booking Created Successfully');
                navigate('/');
            }, (error) => {
                setSubmitting(false);
                alert(error.errMessage || 'Failed to create booking');
            }, dispatch, navigate);
        },
    });

    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const storeName = merchantSettings?.legal_name || 'Online Store';

    return (
        <>
            <Helmet>
                <title>{`Book an Event & Catering | ${storeName}`}</title>
                <meta name="description" content={`Make reservations and book catering or private events at ${storeName}. Reserve your tables easily online.`} />
                {currentOrigin && <link rel="canonical" href={`${currentOrigin}/book-event`} />}
            </Helmet>
            <StaticPageHeader title="Book an Event" />

            <section className="middle bg-light">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 text-center mb-4">
                            <p className="text-muted fs-md" style={{ fontSize: '1.1rem' }}>Please fill in the details below to make a reservation.</p>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        <div className="col-xl-8 col-lg-10 col-md-12 col-sm-12">
                            <form onSubmit={formik.handleSubmit} className="bg-white p-5 shadow-lg" style={{ borderRadius: '20px', border: '1px solid #f1f5f9' }}>
                                <div className="bg-primary text-white p-3 rounded mb-4 shadow-sm" style={{ backgroundColor: 'var(--primary-color)' }}>
                                    <h5 className="ft-bold mb-0 text-white" style={{ letterSpacing: '0.5px' }}><i className="fas fa-user mr-2"></i>CUSTOMER INFORMATION</h5>
                                </div>
                                <div className="row">
                                    <div className="col-12">
                                        <CustomInput label="Customer Name *" name="contact_name" placeholder="John Doe" formik={formik} />
                                    </div>
                                    <div className="col-12">
                                        <PhoneInputGroup
                                            formik={formik}
                                            countryName="country_id"
                                            phoneName="contact_mobile"
                                            countries={countries}
                                            label="Customer Mobile *"
                                        />
                                    </div>
                                    <div className="col-12 mb-4">
                                        <CustomInput label="Customer Email *" name="contact_email" type="email" placeholder="customer@infinyte.com" formik={formik} />
                                    </div>
                                </div>

                                <div className="bg-primary text-white p-3 rounded mb-4 mt-4 shadow-sm" style={{ backgroundColor: 'var(--primary-color)' }}>
                                    <h5 className="ft-bold mb-0 text-white" style={{ letterSpacing: '0.5px' }}><i className="fas fa-chair mr-2"></i>SEAT & OUTLET DETAILS</h5>
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <CustomInput 
                                            as="select" 
                                            label="Select Outlet *" 
                                            name="outlet_id" 
                                            placeholder="Choose an outlet..."
                                            options={outlets.map(o => ({ value: o.id, label: o.name }))}
                                            formik={formik} 
                                        />
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <CustomInput label="Number of Guests *" name="person_count" type="number" placeholder="e.g. 8" formik={formik} />
                                    </div>
                                </div>

                                <div className="bg-primary text-white p-3 rounded mb-4 mt-4 shadow-sm" style={{ backgroundColor: 'var(--primary-color)' }}>
                                    <h5 className="ft-bold mb-0 text-white" style={{ letterSpacing: '0.5px' }}><i className="fas fa-calendar-alt mr-2"></i>DATE & TIME</h5>
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <CustomInput label="Reservation Date *" name="reservation_date" type="date" formik={formik} />
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <CustomInput label="Arrival Time *" name="start_time" type="time" formik={formik} />
                                    </div>
                                </div>

                                <div className="col-12 mt-5 text-center">
                                    <button
                                        type="submit"
                                        className="btn-product-action btn-cart-premium w-100"
                                        style={{ height: '54px', fontSize: '1.1rem', borderRadius: '12px' }}
                                        disabled={formik.isSubmitting || !formik.isValid}
                                    >
                                        {formik.isSubmitting ? (
                                            <>
                                                <i className="fas fa-circle-notch fa-spin mr-2"></i> Processing...
                                            </>
                                        ) : 'Create Booking'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default BookEvent;
